import app from 'flarum/forum/app';

/**
 * The current user's alts, fetched once per page load.
 *
 * Kept outside component state because both the composer and the management
 * page read the same list, and a stale copy in one of them would let someone
 * pick an alt they had just retired.
 */
const state = {
  loaded: false,
  loading: null,
  ids: [],
};

function toUsers(ids) {
  return ids.map((id) => app.store.getById('users', id)).filter(Boolean);
}

export function altsLoaded() {
  return state.loaded;
}

export function allAlts() {
  return toUsers(state.ids);
}

export function activeAlts() {
  return allAlts().filter((alt) => !alt.altRetiredAt());
}

export function retiredAlts() {
  return allAlts().filter((alt) => alt.altRetiredAt());
}

/**
 * Safe to call at any point in the lifecycle.
 *
 * app.session and app.forum do not exist while initializers run — core creates
 * them after the initializer loop — so every accessor here is optional. Callers
 * are all render-time or later, but a guard costs nothing and a boot-time crash
 * takes the whole forum down, not just this extension.
 */
export function canCreateAlts() {
  return !!(app.session && app.session.user && app.forum && app.forum.attribute('canCreateAlts'));
}

export function loadAlts(force = false) {
  // Any logged-in member, not just those who can create alts. Someone whose
  // trust was revoked still owns the characters they made, and still needs to
  // reach them to retire or rename them. The endpoint only ever returns the
  // caller's own alts, so this gives nothing away.
  if (!app.session || !app.session.user) return Promise.resolve([]);
  if (state.loading) return state.loading;
  if (state.loaded && !force) return Promise.resolve(allAlts());

  state.loading = app
    .request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/alts',
    })
    .then((payload) => {
      app.store.pushPayload(payload);

      state.ids = (payload.data || []).map((record) => record.id);
      state.loaded = true;
      state.loading = null;

      m.redraw();

      return allAlts();
    })
    .catch((error) => {
      state.loading = null;
      throw error;
    });

  return state.loading;
}

export function rememberAlt(user) {
  const id = String(user.id());

  if (!state.ids.includes(id)) {
    state.ids.push(id);
  }

  state.loaded = true;
}

export function maxAlts() {
  return (app.forum && app.forum.attribute('nurriemunchersAlts.maxAltsPerUser')) || 0;
}
